import { useState } from "react";
import { toast } from "sonner";
import { Check, X, RefreshCw, Eye, CalendarPlus, AlertTriangle, Image as ImageIcon } from "lucide-react";
import { api, errMsg, inr } from "@/lib/api";
import { StatusBadge } from "@/components/admin/AdminLayout";
import { ITEM_STATUS_LABELS, PLATFORM_LABELS, POST_STATUS_LABELS, fmtIST, media } from "@/lib/smm";

const Btn = ({ children, className = "", ...p }) => (
  <button {...p} className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-lg border transition-colors disabled:opacity-50 ${className}`}>{children}</button>
);

export const ItemCard = ({ item, platforms, onChanged, onPreview, onSchedule }) => {
  const [busy, setBusy] = useState("");
  const act = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); ok && toast.success(ok); onChanged(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const togglePlatform = (p) => {
    const next = item.platforms.includes(p) ? item.platforms.filter((x) => x !== p) : [...item.platforms, p];
    act(`p-${p}`, () => api.put(`/admin/smm/items/${item.id}`, { platforms: next }));
  };
  const status = (s, msg) => act(s, () => api.post(`/admin/smm/items/${item.id}/status`, { status: s }), msg);
  const regen = () => act("regen", () => api.post(`/admin/smm/items/${item.id}/generate`), "New captions written");
  const has = Object.keys(item.content || {}).length > 0;
  return (
    <div className={`bg-white border rounded-2xl p-4 flex flex-col gap-3 ${item.status === "rejected" ? "opacity-60 border-slate-200" : "border-slate-200"}`} data-testid={`smm-item-${item.id}`}>
      <div className="flex gap-3">
        <img src={media(item.formats.square)} alt="" loading="lazy" className="h-24 w-24 rounded-xl object-cover border border-slate-100 shrink-0 bg-slate-50" data-testid={`smm-item-${item.id}-creative`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge s={item.status} label={ITEM_STATUS_LABELS[item.status]} />
            <span className="text-[11px] font-bold uppercase text-slate-400">{item.kind}</span>
            <span className="text-[11px] text-slate-400">pick score {item.pick_score}</span>
          </div>
          <p className="font-semibold text-sm leading-snug mt-1 line-clamp-2" data-testid={`smm-item-${item.id}-title`}>{item.title}</p>
          <p className="text-xs text-slate-500 mt-0.5">
            {item.merchant_name}{item.category_name && ` · ${item.category_name}`}
            {item.price ? ` · ${inr(item.price)}` : ""}{item.mrp > item.price ? ` (MRP ${inr(item.mrp)})` : ""}
            {item.code && <> · code <b>{item.code}</b></>}
          </p>
        </div>
      </div>
      <p className="text-[11px] text-slate-500" data-testid={`smm-item-${item.id}-reasons`}>Why: {item.reasons.join(" · ")}</p>
      {has ? (
        <div className="bg-slate-50 rounded-xl p-3 text-sm">
          <p className="font-bold">{item.content.headline}</p>
          <p className="text-slate-600 text-xs mt-1 line-clamp-3 whitespace-pre-line">{item.content.instagram}</p>
          <p className="text-slate-600 text-xs mt-1">{item.content.hindi_line}</p>
        </div>
      ) : (
        <p className="text-xs text-slate-500 bg-slate-50 rounded-xl p-3">{item.content_error ? <span className="text-red-600">Captions failed: {item.content_error}</span> : "Captions not written yet - press Write captions."}</p>
      )}
      {item.number_warnings?.length > 0 && (
        <p className="text-xs text-amber-800 bg-amber-50 rounded-lg p-2 flex gap-1.5" data-testid={`smm-item-${item.id}-warnings`}><AlertTriangle size={14} className="shrink-0 mt-0.5" /> {item.number_warnings.join("; ")}</p>
      )}
      <div className="flex flex-wrap gap-1.5" data-testid={`smm-item-${item.id}-platforms`}>
        {platforms.map((p) => (
          <button key={p} disabled={busy === `p-${p}`} onClick={() => togglePlatform(p)} data-testid={`smm-item-${item.id}-platform-${p}`}
            className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${item.platforms.includes(p) ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-500 border-slate-200"}`}>
            {PLATFORM_LABELS[p]}
          </button>
        ))}
      </div>
      {item.posts?.length > 0 && (
        <ul className="text-[11px] text-slate-600 space-y-0.5" data-testid={`smm-item-${item.id}-posts`}>
          {item.posts.map((p) => <li key={p.id}>{PLATFORM_LABELS[p.platform]} · {fmtIST(p.scheduled_at)} IST · <b>{POST_STATUS_LABELS[p.status]}</b></li>)}
        </ul>
      )}
      <div className="flex flex-wrap gap-2 mt-auto">
        <Btn onClick={() => onPreview(item)} className="border-slate-200 hover:bg-slate-50" data-testid={`smm-item-${item.id}-preview`}><Eye size={14} /> Preview & edit</Btn>
        <Btn onClick={regen} disabled={busy === "regen"} className="border-slate-200 hover:bg-slate-50" data-testid={`smm-item-${item.id}-regenerate`}>
          <RefreshCw size={14} className={busy === "regen" ? "animate-spin" : ""} /> {has ? "Regenerate" : "Write captions"}
        </Btn>
        {item.status !== "approved" && item.status !== "rejected" && (
          <Btn onClick={() => status("approved", "Approved")} disabled={!has || !!busy} className="bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700" data-testid={`smm-item-${item.id}-approve`}><Check size={14} /> Approve</Btn>
        )}
        {item.status === "approved" && (
          <Btn onClick={() => onSchedule(item)} className="bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700" data-testid={`smm-item-${item.id}-schedule`}><CalendarPlus size={14} /> Schedule</Btn>
        )}
        {item.status !== "rejected" ? (
          <Btn onClick={() => status("rejected", "Rejected - it won't be scheduled")} disabled={!!busy} className="border-red-200 text-red-700 hover:bg-red-50" data-testid={`smm-item-${item.id}-reject`}><X size={14} /> Reject</Btn>
        ) : (
          <Btn onClick={() => status("ready", "Back in review")} disabled={!!busy} className="border-slate-200 hover:bg-slate-50" data-testid={`smm-item-${item.id}-restore`}>Undo reject</Btn>
        )}
        <a href={media(item.formats.story)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-900 px-1" data-testid={`smm-item-${item.id}-designs`}><ImageIcon size={14} /> Story design</a>
      </div>
      <p className="sr-only">{ITEM_STATUS_LABELS[item.status]}</p>
    </div>
  );
};
